import React from 'react';
import { Brain, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export default function TermsPage() {
  useDocumentTitle('Terms of Service');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Navigation */}
      <nav className="border-b border-border-light bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-text-primary">Campus Connect</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-primary-500 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-10 h-10 text-primary-500" />
          <h1 className="text-3xl font-bold text-text-primary">Terms of Service</h1>
        </div>

        <div className="bg-surface-light rounded-xl shadow-md p-8 space-y-8">
          <p className="text-sm text-text-muted">Last updated: March 3, 2026</p>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Agreement to Terms</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              By accessing or using CampusConnect at campusconnectedu.com ("the Service"), you agree to
              be bound by these Terms of Service. If you are using the Service on behalf of a school or
              organization, you represent that you have the authority to bind that organization to these
              terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Description of Service</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              CampusConnect is an AI-powered campus management platform designed for K-12 schools,
              academies, and small colleges. The Service provides communication tools, calendar
              management, task tracking, document storage, attendance tracking, team management,
              and optional LMS integration. The Service is provided as a web application accessible
              through modern web browsers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">User Accounts</h2>
            <ul className="list-disc list-inside space-y-2 text-text-secondary text-sm">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must notify us immediately of any unauthorized access to your account</li>
              <li>One person may not maintain more than one account</li>
              <li>You must be at least 18 years old to create an account (CampusConnect is for school staff, not students)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">School Data Ownership</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              You retain full ownership of all data you and your team enter into CampusConnect, including
              messages, documents, calendar events, tasks, and attendance records. We do not claim any
              intellectual property rights over your content. You grant us a limited license to store,
              process, and display your data solely for the purpose of providing the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Acceptable Use</h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-2">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary text-sm">
              <li>Use the Service for any unlawful purpose</li>
              <li>Upload malicious files or content</li>
              <li>Attempt to gain unauthorized access to other schools' data</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Resell or redistribute the Service without our written consent</li>
              <li>Use automated tools to scrape or extract data from the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Subscriptions & Billing</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              CampusConnect offers free and paid subscription plans. Paid subscriptions are billed
              monthly through Stripe. You can upgrade, downgrade, or cancel your subscription at
              any time from your Billing page. Cancellations take effect at the end of the current
              billing period. We do not offer refunds for partial months, but you will retain access
              to paid features until the end of your billing cycle.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Service Availability</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We strive to maintain high availability of the Service but do not guarantee uninterrupted
              access. We may perform scheduled maintenance with advance notice. We are not liable for
              any downtime caused by factors beyond our reasonable control, including internet outages,
              third-party service disruptions, or force majeure events.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Termination</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              You may delete your account at any time. We may suspend or terminate your access if you
              violate these terms. Upon termination, your personal data will be deleted in accordance
              with our Privacy Policy. School administrators may request a data export before deletion
              by contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Limitation of Liability</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              To the maximum extent permitted by law, CampusConnect and its operators shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages,
              including loss of data, revenue, or business opportunities, arising from your use
              of the Service. Our total liability shall not exceed the amount you paid for the
              Service in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Changes to Terms</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We may update these Terms of Service from time to time. We will notify registered users
              of material changes via email or in-app notification. Continued use of the Service after
              changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Contact</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              For questions about these Terms, contact us at{' '}
              <a href="mailto:support@sketchmyapp.com" className="text-primary-500 hover:underline">
                support@sketchmyapp.com
              </a>.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-text-muted">
          <div className="flex justify-center gap-6">
            <Link to="/privacy" className="hover:text-primary-500 transition-colors">Privacy Policy</Link>
            <Link to="/support" className="hover:text-primary-500 transition-colors">Support</Link>
            <Link to="/getting-started" className="hover:text-primary-500 transition-colors">Getting Started</Link>
          </div>
        </div>
      </div>
    </div>
  );
}